import { FC } from 'react';
import { Button, FormHelperText, IconTrash, Tabs, Typography } from '@screentone/core';

import { MobileIssueDtoArtifactFragment } from 'data/generated/graphql';
import { formatDateToShortDateTime } from 'utils/time';
import styles from './IssueSectionsPreview.module.scss';
import { ScheduledFromQuery } from '../hooks/useScheduling.utils';
import { MobileIssueSectionSummary } from '../MobileIssueManagementEdit.utils';

type Props = {
  tabIndex: number;
  onTabChange: (tab: number) => void;
  handleSetScheduledAsCurrent: (scheduled: ScheduledFromQuery) => () => void;
  handleDeleteScheduledSection: (scheduled: ScheduledFromQuery) => () => void;
  issue: MobileIssueDtoArtifactFragment | null;
  originalIssue: MobileIssueDtoArtifactFragment | null;
  scheduledSections?: ScheduledFromQuery[] | null;
};

function getSectionSummaries(issue: MobileIssueDtoArtifactFragment | null): MobileIssueSectionSummary[] {
  if (!issue) return [];
  return issue.root.collection.map((section) => ({
    id: section.allessehId ?? '',
    sectionLabel: section.attributes.sectionLabel
  }));
}

export const IssueSectionsPreview: FC<Props> = ({
  tabIndex,
  onTabChange,
  handleSetScheduledAsCurrent,
  handleDeleteScheduledSection,
  issue,
  originalIssue,
  scheduledSections
}) => {
  const currentIssue = tabIndex === 0 ? originalIssue : issue;
  const sections = currentIssue ? getSectionSummaries(currentIssue) : [];
  const isSectionsTab = tabIndex === 0 || tabIndex === 1;

  return (
    <div className={styles.issueSectionsPreview}>
      <Tabs role="tablist" value={tabIndex} onChange={onTabChange}>
        <Tabs.Item role="tab" id="tab-id0" aria-selected={tabIndex === 0} aria-controls="tabpanel-id0">
          Original Issue
        </Tabs.Item>
        <Tabs.Item role="tab" id="tab-id1" aria-selected={tabIndex === 1} aria-controls="tabpanel-id1">
          Updated Issue
        </Tabs.Item>
        <Tabs.Item role="tab" id="tab-id2" aria-selected={tabIndex === 2} aria-controls="tabpanel-id2">
          Scheduled Issues
        </Tabs.Item>
      </Tabs>

      {currentIssue && isSectionsTab && (
        <div className={styles.issuePreview}>
          <ol className={styles.issuePreviewList}>
            {sections.map((section, idx) => (
              <li key={section.id} className={styles.issuePreviewListItem}>
                <Typography weight="bold">
                  {idx + 1}. {section.sectionLabel ?? '(No label)'}
                </Typography>
                <Typography size="sm" color="asphalt">
                  {section.id}
                </Typography>
              </li>
            ))}
          </ol>
        </div>
      )}

      {tabIndex === 2 && (
        <div className={styles.issuePreview}>
          <FormHelperText>Click a scheduled section to set it as current.</FormHelperText>
          <FormHelperText>You cannot edit scheduled sections but you can delete and recreate.</FormHelperText>
          {scheduledSections && scheduledSections.length > 0 ? (
            scheduledSections.map((scheduledSection) => (
              <div key={scheduledSection.publishUtc} className={styles.issuePreviewListItem}>
                <Button tertiary onClick={handleSetScheduledAsCurrent(scheduledSection)}>
                  {formatDateToShortDateTime(scheduledSection.publishUtc)}
                </Button>
                <Button
                  tertiary
                  icon={IconTrash}
                  color="lava"
                  onClick={handleDeleteScheduledSection(scheduledSection)}
                />
              </div>
            ))
          ) : (
            <Typography>No scheduled sections found.</Typography>
          )}
        </div>
      )}
    </div>
  );
};
